import { ApiResponse, BetRestriction } from '../models';
import http from '../utils/http';

interface BackendAuthToken {
  name: string;
  url: string;
  token: string;
}

const backendTokensStorageKey = 'admin-backend-auth-tokens';

function getBackendAuthTokens(): BackendAuthToken[] {
  try {
    const stored = localStorage.getItem(
      backendTokensStorageKey
    );

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getBackend(
  backendName: string
): BackendAuthToken {
  const backends = getBackendAuthTokens();

  const backend = backends.find(
    (item) => item.name === backendName
  );

  if (!backend) {
    throw new Error(
      `Backend "${backendName}" is not authenticated`
    );
  }

  return backend;
}

function getBackendUrl(
  backend: BackendAuthToken,
  path: string
): string {
  return `${backend.url.replace(/\/$/, '')}${path}`;
}

function getBackendConfig(
  backend: BackendAuthToken
): any {
  return {
    backend: backend.name,
  };
}

/**
 * GET restriction constants.
 *
 * Constants are the same for the selected backend,
 * so we use the first authenticated backend.
 */
export async function getRestrictionFields(): Promise<any> {
  const backends = getBackendAuthTokens();

  if (!backends.length) {
    return [];
  }

  const backend = backends[0];

  const response: ApiResponse<any> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/constants'
      ),
      getBackendConfig(backend)
    );

  return response.data;
}

/**
 * Create restriction on selected backend.
 */
export async function createRestriction(
  reqData: { [key: string]: any },
  idManager: number,
  backendName: string,
): Promise<BetRestriction> {
  const backend = getBackend(backendName);

  const response: ApiResponse<BetRestriction> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction'
      ),
      {
        ...reqData,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Edit restriction on selected backend.
 */
export async function editRestriction(
  id: any,
  reqData: { [key: string]: any },
  idManager: number,
  backendName: string,
): Promise<BetRestriction> {
  const backend = getBackend(backendName);

  const response: ApiResponse<BetRestriction> =
    await http.put(
      getBackendUrl(
        backend,
        `/api/events/bet-restriction/edit/${id}`
      ),
      {
        ...reqData,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Delete restriction on selected backend.
 */
export async function deleteRuleService(
  ruleId: number,
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  return await http.delete(
    getBackendUrl(
      backend,
      `/api/events/bet-restriction/id/${ruleId}`
    ),
    {
      ...getBackendConfig(backend),
      params: {
        idManager: Number(idManager),
      },
      headers: {
        successAlert: true,
      },
    },
  );
}

/**
 * Get all restrictions from selected backend.
 */
export async function getAllRestrictions(
  idManager: number,
  backendName?: string,
): Promise<BetRestriction[]> {
  if (backendName) {
    const backend = getBackend(backendName);

    const response: ApiResponse<BetRestriction[]> = await http.get(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/all'
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idManager: Number(idManager),
        },
      }
    );

    return response.data.map((restriction: any) => ({
      ...restriction,
      backendName: backend.name,
      backendUrl: backend.url,
    }));
  }

  const stored = localStorage.getItem('admin-backend-auth-tokens');

  if (!stored) {
    return [];
  }

  const backends: {
    name: string;
    url: string;
    token: string;
  }[] = JSON.parse(stored);

  const results = await Promise.all(
    backends.map(async (backend) => {
      const response: ApiResponse<BetRestriction[]> = await http.get(
        getBackendUrl(
          backend,
          '/api/events/bet-restriction/all'
        ),
        {
          ...getBackendConfig(backend),
          params: {
            idManager: Number(idManager),
          },
        }
      );

      return response.data.map((restriction: any) => ({
        ...restriction,
        backendName: backend.name,
        backendUrl: backend.url,
      }));
    })
  );

  return results.flat();
}

/**
 * Get event restrictions from selected backend.
 */
export async function getEventRestrictions(
  eventId: number,
  idManager: number,
  backendName: string,
): Promise<BetRestriction[]> {
  const backend = getBackend(backendName);

  const response: ApiResponse<BetRestriction[]> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/restrictions/${eventId}`
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idManager: Number(idManager),
        },
      }
    );

  return response.data;
}

/**
 * Set event restrictions on selected backend.
 */
export async function serviceSetEventRestrictions(
  eventIds: number[],
  betRestrictionsIds: number[],
  idManager: number,
  notWithEventIds?: number[],
  isBulk: boolean = false,
  backendName?: string,
): Promise<any> {
  if (!backendName) {
    throw new Error(
      'backendName is required for serviceSetEventRestrictions'
    );
  }

  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/set/events'
      ),
      {
        eventIds,
        betRestrictionsIds,
        notWithEventIds,
        isBulk,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Bulk block/unblock events on selected backend.
 */
export async function serviceBulkBlockUnblock(
  eventIds: number[],
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/bulk-block/switch'
      ),
      {
        eventIds,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Bulk set events status on selected backend.
 */
export async function serviceBulkSetEventsStatus(
  eventIds: number[],
  idStatus: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/bulk/status'
      ),
      {
        eventIds,
        idStatus,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Bulk set events start time on selected backend.
 */
export async function serviceBulkSetEventsStartTime(
  eventIds: number[],
  startTime: string,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.put(
      getBackendUrl(
        backend,
        '/api/events/bulk/start-time'
      ),
      {
        eventIds,
        startTime,
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Get competition restrictions from selected backend.
 */
export async function getCompetitionRestrictions(
  competitionId: number,
  idManager: number,
  backendName: string,
): Promise<BetRestriction[]> {
  const backend = getBackend(backendName);

  const response: ApiResponse<BetRestriction[]> =
    await http.get(
      getBackendUrl(
        backend,
        `/api/events/competition/restrictions/${competitionId}`
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idManager: Number(idManager),
        },
      },
    );

  return response.data;
}

/**
 * Set competition restrictions on selected backend.
 */
export async function setCompetitionRestrictions(
  competitionId: number[],
  restrictionIds: number[],
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/set/competition'
      ),
      {
        competitionIds: competitionId,
        betRestrictionsIds: restrictionIds,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Bulk set restrictions on competitions.
 */
export async function setBulkRestrictionsOnCompetitions(
  competitionIds: number[],
  betRestrictionsIds: number[],
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/bulk/set/competition'
      ),
      {
        competitionIds,
        betRestrictionsIds,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Set restrictions on competition organisers.
 */
export async function setBulkRestrictionsOnCompetitionsOrganizers(
  competitionOrganiserIds: number[],
  betRestrictionsIds: number[],
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/set/competition-organiser'
      ),
      {
        competitionOrganiserIds,
        betRestrictionsIds,
        idManager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Set sport restrictions on selected backend.
 */
export async function setSportRestrictions(
  competitionTypeIds: number[],
  betRestrictionIds: number[],
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.post(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/set/sport'
      ),
      {
        competition_type_ids:
          competitionTypeIds.map(Number),
        bet_restrictions_ids:
          betRestrictionIds.map(Number),
        id_manager: Number(idManager),
      },
      {
        ...getBackendConfig(backend),
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Remove sport restriction from selected backend.
 */
export async function removeSportRestriction(
  idCompetitionType: number,
  idBetRestriction: number,
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.delete(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/remove/sport'
      ),
      {
        ...getBackendConfig(backend),
        params: {
          id_competition_type:
            Number(idCompetitionType),
          id_bet_restriction:
            Number(idBetRestriction),
          id_manager: Number(idManager),
        },
        headers: {
          successAlert: true,
        },
      },
    );

  return response.data;
}

/**
 * Get limits groups from selected backend.
 */
export async function getLimitsGroups(
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/groups'
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idManager: Number(idManager),
        },
      },
    );

  return response.data;
}

/**
 * Get limits by groups from selected backend.
 */
export async function getLimitsByGroups(
  idManager: number,
  backendName: string,
): Promise<BetRestriction> {
  const backend = getBackend(backendName);

  const response: ApiResponse<any> =
    await http.get(
      getBackendUrl(
        backend,
        '/api/events/bet-restriction/by-groups'
      ),
      {
        ...getBackendConfig(backend),
        params: {
          idManager: Number(idManager),
        },
      },
    );

  return response.data;
}

/**
 * Add group on selected backend.
 */
export async function addGroupService(
  name: string,
  idManager: number,
  backendName: string,
): Promise<any> {
  const backend = getBackend(backendName);

  const response = await http.post(
    getBackendUrl(
      backend,
      '/api/events/bet-restriction/group'
    ),
    {
      name,
      idManager: Number(idManager),
    },
    {
      ...getBackendConfig(backend),
      headers: {
        successAlert: true,
      },
    },
  );

  return response.data;
}